import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useWeightUnit } from "@/utils/weight-unit";
import { Link } from "@tanstack/react-router";
import {
  Calculator,
  ChevronRight,
  Dumbbell,
  Info,
  Scale,
  Settings2,
  Sparkles,
} from "lucide-react";
import * as React from "react";
import { useState } from "react";

interface ProgramSettingsDialogProps {
  children?: React.ReactNode;
}

export function ProgramSettingsDialog({
  children,
}: ProgramSettingsDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { unit, setUnit } = useWeightUnit();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 rounded-lg border-border/60 bg-background/80 px-2.5 font-medium text-muted-foreground text-xs shadow-none backdrop-blur-sm transition-colors hover:border-border hover:bg-muted/60 hover:text-foreground active:scale-[0.98]"
          >
            <Settings2 className="h-3.5 w-3.5" />
            <span>Ayarlar</span>
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="flex max-h-[80vh] w-[calc(100%-1.5rem)] flex-col overflow-hidden rounded-2xl border-border/80 bg-card p-0 shadow-2xl sm:max-h-[85vh] sm:max-w-md">
        {/* Fixed Header */}
        <div className="shrink-0 border-border/60 border-b px-4 py-3.5 sm:px-6 sm:py-4">
          <DialogHeader className="pr-8 text-left">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/80 bg-muted/40 text-foreground">
                <Settings2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate font-bold text-base text-foreground sm:text-lg">
                  Program Ayarları
                </DialogTitle>
                <DialogDescription className="truncate text-muted-foreground text-xs">
                  Birimler ve antrenman tercihleri
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Scrollable Content Body */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
          {/* Section: Birimler & Ölçü */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              Birimler & Ölçü
            </h4>

            <div className="divide-y divide-border/40 rounded-xl border border-border/60 bg-muted/15">
              {/* Weight Unit Selection */}
              <div className="space-y-2.5 p-3 sm:p-3.5">
                <div className="flex items-center gap-2">
                  <Scale className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-xs sm:text-sm">
                      Ağırlık Birimi
                    </p>
                  </div>
                </div>

                {/* Full-width Responsive Segmented Control */}
                <div className="grid grid-cols-2 gap-1 rounded-xl border border-border/60 bg-muted/50 p-1">
                  <button
                    type="button"
                    onClick={() => setUnit("kg")}
                    className={cn(
                      "flex items-center justify-center gap-1 rounded-lg py-2 font-bold text-xs transition-all active:scale-[0.98]",
                      unit === "kg"
                        ? "bg-background text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <span>KG</span>
                    <span className="font-normal text-[10px] opacity-70">
                      (Kilogram)
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnit("lbs")}
                    className={cn(
                      "flex items-center justify-center gap-1 rounded-lg py-2 font-bold text-xs transition-all active:scale-[0.98]",
                      unit === "lbs"
                        ? "bg-background text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <span>LBS</span>
                    <span className="font-normal text-[10px] opacity-70">
                      (Pound)
                    </span>
                  </button>
                </div>
              </div>

              {/* Step & Rounding Info */}
              <div className="flex items-center justify-between gap-3 p-3 sm:p-3.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Calculator className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-xs sm:text-sm">
                      Yuvarlama Adımı
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {unit === "kg"
                        ? "2.5 kg hassasiyeti"
                        : "5.0 lbs hassasiyeti"}
                    </p>
                  </div>
                </div>

                <Badge
                  variant="secondary"
                  className="shrink-0 font-mono font-semibold text-xs"
                >
                  {unit === "kg" ? "2.5 kg" : "5.0 lbs"}
                </Badge>
              </div>
            </div>
          </div>

          {/* Section: Güç & 1RM Değerleri */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              Güç & 1RM Değerleri
            </h4>

            <div className="divide-y divide-border/40 rounded-xl border border-border/60 bg-muted/15">
              {/* 1RM Sync info */}
              <div className="flex items-center justify-between gap-3 p-3 sm:p-3.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Dumbbell className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-xs sm:text-sm">
                      Maksimum Ağırlıklar
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      1RM değerlerini düzenle
                    </p>
                  </div>
                </div>

                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 gap-1 rounded-lg px-2.5 text-xs"
                >
                  <Link to="/profile">
                    <span>Yönet</span>
                    <ChevronRight className="h-3 w-3" />
                  </Link>
                </Button>
              </div>

              {/* Smart RPE calculation info */}
              <div className="flex items-center justify-between gap-3 p-3 sm:p-3.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-xs sm:text-sm">
                      Hedef Ağırlıklar
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      RPE ve 1RM otomatik hesap
                    </p>
                  </div>
                </div>

                <Badge
                  variant="outline"
                  className="shrink-0 border-border/80 font-medium text-[11px]"
                >
                  Otomatik
                </Badge>
              </div>
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2 rounded-xl border border-border/50 bg-muted/20 p-2.5 text-[11px] text-muted-foreground leading-relaxed">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/80" />
            <p>
              Birim değişiklikleri tüm antrenman tablolarınıza anında
              yansıtılır.
            </p>
          </div>
        </div>

        {/* Fixed Footer (Always visible) */}
        <div className="flex shrink-0 items-center justify-between border-border/60 border-t bg-muted/20 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
            <span>Birim:</span>
            <span className="font-bold text-foreground uppercase">{unit}</span>
          </div>
          <Button
            variant="default"
            size="sm"
            className="h-8 rounded-lg px-5 font-semibold text-xs shadow-sm"
            onClick={() => setIsOpen(false)}
          >
            Kapat
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
